import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import jurisdictionService from '../services/jurisdictionService';

export const JURISDICTIONS_KEY = ['jurisdictions'];

/**
 * Hook to fetch all jurisdictions
 */
export function useJurisdictions(options = {}) {
  return useQuery({
    queryKey: [...JURISDICTIONS_KEY, options],
    queryFn: () => jurisdictionService.getJurisdictions(options),
    staleTime: 1000 * 60 * 30, // 30 minutes - jurisdictions don't change often
    cacheTime: 1000 * 60 * 60 // 1 hour
  });
}

/**
 * Hook to fetch a single jurisdiction
 */
export function useJurisdiction(code) {
  return useQuery({
    queryKey: [...JURISDICTIONS_KEY, code],
    queryFn: () => jurisdictionService.getJurisdiction(code),
    enabled: !!code,
    staleTime: 1000 * 60 * 30
  });
}

/**
 * Hook to create a jurisdiction (admin only)
 */
export function useCreateJurisdiction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => jurisdictionService.createJurisdiction(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: JURISDICTIONS_KEY });
    }
  });
}

/**
 * Hook to update a jurisdiction (admin only)
 */
export function useUpdateJurisdiction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ code, data }) => jurisdictionService.updateJurisdiction(code, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: JURISDICTIONS_KEY });
    }
  });
}

/**
 * Hook to delete a jurisdiction (admin only)
 */
export function useDeleteJurisdiction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (code) => jurisdictionService.deleteJurisdiction(code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: JURISDICTIONS_KEY });
    }
  });
}

export default {
  useJurisdictions,
  useJurisdiction,
  useCreateJurisdiction,
  useUpdateJurisdiction,
  useDeleteJurisdiction
};
